/**
 * CellProvenance — tiny chip that appears next to a DataRow value
 * when the audit trail has a matching entry.
 *
 * Two variants:
 *   - University edit  → green tick,   tooltip "Verified by <Uni> · <date>"
 *   - Staff edit + URL → purple info,  tooltip "Sourced · <date> · <host>" (clickable)
 *
 * Hydrates from `DegreePageContext.auditByField`, a map keyed
 * `${section}::${field_label}` populated once per page-load by
 * `DegreePageProvider`. Zero N+1.
 */
import { useContext, useMemo } from "react";
import { BadgeCheck, Link as LinkIcon } from "lucide-react";
import { DegreePageContext } from "@/components/app/degree/DegreeDetailBits";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

function fmt(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

export default function CellProvenance({ section, field }) {
  const ctx = useContext(DegreePageContext);
  const auditByField = ctx?.auditByField;

  const entry = useMemo(() => {
    if (!auditByField || !section || !field) return null;
    return auditByField.get(`${section}::${field}`) || null;
  }, [auditByField, section, field]);

  if (!entry) return null;

  const isUni = entry.actor_type === "university";
  const dateStr = fmt(entry.created_at);
  const tooltip = isUni ? (
    <span>
      Verified by <strong>{entry.display_name}</strong>
      {dateStr && (
        <>
          <span> · </span>
          {dateStr}
        </>
      )}
    </span>
  ) : (
    <span>
      Sourced{dateStr && ` · ${dateStr}`}
      {entry.source_url && (
        <>
          {" · "}
          <a
            href={entry.source_url}
            target="_blank"
            rel="noreferrer"
            className="underline hover:text-white"
          >
            {entry.display_name}
          </a>
        </>
      )}
    </span>
  );

  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            data-testid={`cell-provenance-${section}-${field}`}
            data-actor-type={entry.actor_type}
            className={`inline-flex items-center align-middle ml-1 cursor-help ${
              isUni ? "text-emerald-600" : "text-purple-700/70"
            }`}
            aria-label={
              isUni
                ? `Verified by ${entry.display_name}`
                : "Sourced from an external URL"
            }
          >
            {isUni ? (
              <BadgeCheck className="h-3 w-3" strokeWidth={2.5} />
            ) : (
              <LinkIcon className="h-3 w-3" strokeWidth={2} />
            )}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-[11px]">
          {tooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
